import type { NodeChipFragment } from '@aurora/shared-generated/types/graphql-types';
import { NodeType } from '@aurora/shared-types/nodes/enums';
import React from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import { FocusElementType } from '../../../types/enums';
import Button from '../../common/Button/Button';
import { ButtonVariant } from '../../common/Button/enums';
import { IconSize } from '../../common/Icon/enums';
import NodeIcon from '../../nodes/NodeIcon/NodeIcon';
import NodeTitle from '../../nodes/NodeTitle/NodeTitle';
import type { GenericNode, NodePickerNode } from '../types';
import localStyles from './NodeSelectorDropdownItem.module.pcss';
import NodeSelectorDropdownItemSubtitle from './NodeSelectorDropdownItemSubtitle';
import AdvancedDropdownExpandButton from '../../advancedDropdown/AdvancedDropdownExpandButton/AdvancedDropdownExpandButton';

/**
 * Props used to disable and style dropdown items in the NodeSelectorDropdownMenu
 */
export interface NodeSelectorDropdownItemDisabledProps {
  /**
   * Callback function that disables the dropdown item based off of properties of the node
   * @param node the node
   */
  isDisabledFn: (node: NodePickerNode) => boolean | null;
  /**
   * When provided, displays a title on hover indicating why the item is disabled
   */
  disabledReasonText?: (node?: Pick<GenericNode, 'id'>) => string | null;
  /**
   * When provided, displays a title on hover indicating why the parent is disabled
   */
  parentDisabledReasonText?: string | null;
  /**
   * When true, sets a lower opacity for the disabled item's node icon
   */
  useDisabledOpacity?: boolean;
  /**
   * When true, sets a lower opacity for the disabled item's title
   */
  useDisabledOpacityForTitle?: boolean;
}

/**
 * Props used to display info popover and style icon in the NodeSelectorDropdownMenu
 */
export interface NodeSelectedItemInfoPopoverProps {
  /**
   * Callback function that adds the info popover based off of properties of the node
   * @param node the node
   */
  showInfoPopoverFn: (node: NodeChipFragment) => boolean | null;
  /**
   * Displays a title on hover indicating the info text
   */
  infoText: (node: NodeChipFragment) => string;
  /**
   * The class name used for this info popover
   */
  className?: string;
}

interface Props {
  /**
   * The node
   */
  node: NodePickerNode;
  /**
   * Whether the node is the currently selected node in the NodeSelectorDropdown
   */
  isSelected: boolean;
  /**
   * Whether the search form is enabled for the dropdown menu
   */
  searchEnabled: boolean;
  /**
   * Props used to disable and style dropdown items in the NodeSelectorDropdownMenu
   */
  disabledProps?: NodeSelectorDropdownItemDisabledProps;
  /**
   * Callback function invoked when the dropdown item is selected
   * @param node the node
   */
  onSelect: (node: NodePickerNode) => void;
  /**
   * Callback function invoked when a keydown event occurs from the dropdown item
   * @param node the node
   * @param event the keydown event
   */
  onKeyDown: (node: NodePickerNode, event: React.KeyboardEvent<HTMLButtonElement>) => void;
  /**
   * Callback function invoked when the "expand node" button is pressed
   * @param node the node
   * @param event the onClick event
   */
  onExpandNode: (
    node: NodePickerNode,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  /**
   * The index of the dropdown item
   */
  idx: number;
}

/**
 * Renders the node in a dropdown item used by the NodeSelectorDropdownMenu
 *
 * @author Jonathan Bridges
 */
const NodeSelectorDropdownItem: React.FC<React.PropsWithChildren<Props>> = ({
  node,
  isSelected,
  searchEnabled,
  disabledProps = {
    isDisabledFn: null,
    useDisabledOpacity: false,
    useDisabledOpacityForTitle: false,
    disabledReasonText: () => null
  },
  onSelect,
  onKeyDown,
  onExpandNode,
  idx
}) => {
  const cx = useClassNameMapper(localStyles);
  const {
    isDisabledFn,
    useDisabledOpacity,
    useDisabledOpacityForTitle,
    disabledReasonText = () => null
  } = disabledProps;
  const isDisabled: boolean = !!isDisabledFn && isDisabledFn(node);
  const useDisabledStyles: boolean = isDisabled && useDisabledOpacity;
  const useDisabledStylesForTitle: boolean = isDisabled && useDisabledOpacityForTitle;

  const shouldRenderNodeNavigationIcon = node.nodeType !== NodeType.BOARD && !searchEnabled;

  return (
    <Dropdown.Item
      as="section"
      disabled={isDisabled}
      className={cx('lia-dropdown-item')}
      onSelect={() => onSelect(node)}
      data-testid={`NodeSelectorDropdownItem.${node.id}`}
      active={!searchEnabled && isSelected}
    >
      <Button
        disabled={isDisabled}
        variant={ButtonVariant.UNSTYLED}
        role="menuitem"
        title={isDisabled ? disabledReasonText(node) : undefined}
        onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => onKeyDown(node, event)}
        data-value={`${FocusElementType.NODE_DETAIL}-${idx}`}
        data-node-selector-btn={idx}
        data-testid={`NodeSelectorDropdownItem.NodeButton.${node.id}`}
        className={cx(
          'lia-btn',
          {
            'border-right': shouldRenderNodeNavigationIcon
          },
          { 'align-items-start': searchEnabled },
          { 'lia-hidden-disabled': !useDisabledStyles },
          { 'lia-hidden-disabled-title': !useDisabledStylesForTitle }
        )}
      >
        <NodeIcon node={node} className={cx('lia-node-icon')} size={IconSize.PX_16} />
        <NodeTitle node={node} as="span" className={cx('lia-title lia-g-clamp')} />
        {searchEnabled && <NodeSelectorDropdownItemSubtitle node={node} />}
      </Button>
      {shouldRenderNodeNavigationIcon && (
        <AdvancedDropdownExpandButton
          idx={idx}
          onClick={event => onExpandNode(node, event)}
          onKeyDown={event => onKeyDown(node, event)}
          testId={`NodeSelectorDropDownItem.ExpandButton.${node.id}`}
          nodeTitle={node.title}
        />
      )}
    </Dropdown.Item>
  );
};
export default NodeSelectorDropdownItem;
